import { horizontalRowLine, LabelGeneralInfo, ListGeneralInfo, ListItemGeneralInfo, StaticDataContainer, Subtitle, ValueGeneralInfo } from '../../styles'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { NotificationReducer } from 'types/notification'
import { getCorIncidente, getDamage, getDateTime, } from 'utils/damage'
import { getLabelByValue, getFactorLabels } from '../../helper'
import { getVarible } from 'utils/variblesHandler'
import { VariableAction, VariableReducer } from 'types/variable'
import VariableActions from 'actions/Variable'
import { Dispatch } from 'redux'
import { dateStringFormat } from 'utils/dateUtils'
import type { LondonProtocolStaticDataProps } from './staticDataSharedProps'

export default function StaticDataV2({
    londonProtocol: protocol,
    context = 'individual',
    groupKeyDisplay = ''
}: LondonProtocolStaticDataProps) {
    const isGroup = context === 'group'
    const LONDON_PROTOCOL_VERSION = 'v2'
    const [typesOfDiagnosis, setTypesOfDiagnosis] = useState([]);
    const [omsClassifications, setOmsClassifications] = useState([])

    const variableDispatch: Dispatch<VariableAction> = useDispatch()

    const variableSelector: VariableReducer = useSelector(
        (state: RootReducerState) => state.variableReducers
    )
    const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)
    const notification = useMemo(() => notificationSelector?.notificationById, [notificationSelector?.notificationById])
    const londonVaribles = useMemo(() => notificationSelector?.londonVaribles, [notificationSelector?.londonVaribles])
    const protocoloNotificationFactors = useMemo(() => {
        const keys = Object.keys(notificationSelector?.factors || {});
        let response: Record<string, any[]> = {};
        keys.forEach(k => {
            response[k] = notificationSelector?.factors?.[k]?.filter(
                f => f?.u_category_factors_version === LONDON_PROTOCOL_VERSION
            ) || [];
        });
        return response;
    }, [notificationSelector?.factors, notificationSelector?.londonProtocol]);

    useEffect(() => {
        VariableActions.getAllVariables(variableDispatch)
    }, [])

    useEffect(() => {
        if (notificationSelector.typesOfDiagnosis) {
            const typesOfDiagnosisFlat = notificationSelector.typesOfDiagnosis[0]?.result.flat()
            let typesOfDiagnosis = typesOfDiagnosisFlat?.map((item, index) => ({
                label: item.description,
                value: item.sys_id
            })).sort((o1, o2) => o1.label?.localeCompare(o2.label));
            setTypesOfDiagnosis(typesOfDiagnosis)
        }
    }, [notificationSelector.typesOfDiagnosis])

    const checkBoxData = useMemo(() => {
        console.log("Loading Factors Categories to London Protocol: ", LONDON_PROTOCOL_VERSION)
        return [
            {
                title: 'Fatores do paciente',
                titleValue: protocol?.patient_factors,
                descriptionValue: protocol?.u_patient_factors_description,
                mainObject: protocoloNotificationFactors?.patient_factors
            },
            {
                title: 'Fatores individuais (colaboradores)',
                titleValue: protocol?.individual_factors_people,
                descriptionValue: protocol?.u_individual_factors_people_description,
                mainObject: protocoloNotificationFactors?.individual_factors_people
            },
            {
                title: 'Tarefas',
                titleValue: protocol?.task_factors_and_technology,
                descriptionValue: protocol?.u_task_factors_and_technology_description,
                mainObject: protocoloNotificationFactors?.task_factors_and_technology
            },
            {
                title: 'Fatores da equipe',
                titleValue: protocol?.team_factors_teams,
                descriptionValue: protocol?.u_team_factors_teams_description,
                mainObject: protocoloNotificationFactors?.team_factors_teams
            },
            {
                title: 'Fatores do ambiente de trabalho',
                titleValue: protocol?.work_environment_factors,
                descriptionValue: protocol?.u_work_environment_factors_description,
                mainObject: protocoloNotificationFactors?.work_environment_factors
            },
            {
                title: 'Tecnologia e sistemas eletrônicos de informação',
                titleValue: protocol?.u_technology_and_electronic_information_systems,
                descriptionValue: protocol?.u_description_technology_and_electronic_information_systems,
                mainObject: protocoloNotificationFactors?.technology_and_electronic_information_systems
            },
            {
                title: 'Fatores organizacionais, gerenciais e culturais',
                titleValue: protocol?.organizational_and_managerial_factors,
                descriptionValue: protocol?.u_organizational_and_managerial_factors_description,
                mainObject: protocoloNotificationFactors?.organizational_and_managerial_factors
            },
            {
                title: 'Fatores de contexto institucional',
                titleValue: protocol?.institutional_context_factors,
                descriptionValue: protocol?.u_institutional_context_factors_description,
                mainObject: protocoloNotificationFactors?.institutional_context_factors
            },
            {
                title: 'Apoio a pacientes, familiares e equipe',
                titleValue: protocol?.u_support_for_patients_families_and_staff,
                descriptionValue: protocol?.u_description_support_for_patients_families_and_staff,
                mainObject: protocoloNotificationFactors?.support_for_patients_families_and_staff
            },
        ]
    }, [protocol, londonVaribles])

    useEffect(() => {
        if (variableSelector.variablesList) {
            const varible = getVarible(
                variableSelector.variablesList,
                'containerType',
                'container'
            )
            if (varible?.children) {
                const omsChoices = getVarible(
                    varible?.children,
                    'name',
                    'u_oms_classification'
                )

                setOmsClassifications(omsChoices?.choices)
            }
        }
    }, [variableSelector])

    const getTreatmentNumber = useMemo(() => {
        if (notification?.document_type?.value === 'atendimento') {
            if (protocol?.u_patient_care_number) {
                return protocol?.u_patient_care_number || "-";
            } else {
                return notification?.document_number?.value;
            }
        } else
        if (notification?.document_type?.value === 'prontuario') {
            return protocol?.u_patient_care_number || "-";
        }

        return " - ";
    },[notification?.document_type])

    const getMedicalRecord =  useMemo(() => {
        if (notification?.document_type?.value === 'prontuario') {
            if (notification?.document_number?.value) {
                return notification?.document_number?.value;
            }
        }
        return " - ";
    }, [notification?.document_type])

    const getSentinelEvent = useMemo(() => {
            if(notificationSelector?.sentinelEvent){
                const notificationVersion = notification?.u_version?.value || process.env.REACT_APP_NOTIFICATION_VERSION
                const sentinelEventValue = notificationSelector.notificationById?.u_sentinel_event_jci?.value;
                const [sentinelEvent] = notificationSelector?.sentinelEvent?.filter(se => se?.u_version === notificationVersion)                                                            .filter(se => se?.sys_id == sentinelEventValue)
                return sentinelEvent?.description || "-";
            }
            
            return "-";
        }, [notificationSelector?.sentinelEvent,notificationSelector.notificationById?.u_sentinel_event_jci?.value])
    
    
    const getNeverEvent = useMemo(() => {
        if (notificationSelector?.neverEvents) {
            const notificationVersion = notification?.u_version?.value || process.env.REACT_APP_NOTIFICATION_VERSION
            const neverEventsReference = notificationSelector.notificationById?.u_never_events_reference?.value;
            const [neverEvent] = notificationSelector?.neverEvents?.filter(se => se?.u_version === notificationVersion)
                                                                    .filter(se => se?.sys_id == neverEventsReference)
            return neverEvent?.u_description || "-";
        }
        return "-";
    }, [notificationSelector?.neverEvents, notificationSelector.notificationById?.u_never_events_reference?.value])

    const getChronologicalReport = () => {
        return protocol?.chronological_report?.split('\n').map(e => e);
    }

    return (
        <StaticDataContainer id="staticDataContainer" data-testid="test-static-data-container">
            <div className="flex flex-row gap-12 mt-4 mb-4">
                <div className="flex flex-col ">
                    <div>
                        <LabelGeneralInfo>{isGroup ? 'Chave do grupo' : 'Chave da notificação'}</LabelGeneralInfo>
                        <ValueGeneralInfo>
                            {isGroup
                                ? groupKeyDisplay?.trim()
                                    ? groupKeyDisplay.trim()
                                    : '—'
                                : notification?.number?.display_value}
                        </ValueGeneralInfo>
                    </div>
                    <div>
                        <span
                            className={`chip-incidente mt-4 incidente-${getCorIncidente(
                                getDamage(notification?.damage_level?.value)
                                    ?.value
                            )}`}
                        >
                            {`${getDamage(notification?.damage_level?.value)
                                ?.label
                                }`}
                        </span>
                    </div>
                    <div className='mt-8'>
                        <LabelGeneralInfo>Iniciais do nome do paciente</LabelGeneralInfo>
                        <ValueGeneralInfo style={{ wordBreak: 'break-all' }}>
                            {notification?.patient_initials?.display_value}
                        </ValueGeneralInfo>
                    </div>
                    <div>
                        <LabelGeneralInfo>Convênio</LabelGeneralInfo>
                        <ValueGeneralInfo>{protocol?.patients_health_insurance}</ValueGeneralInfo>
                    </div>
                </div>
                <div className="flex flex-col">
                    <div>
                        <LabelGeneralInfo>Data da notificação</LabelGeneralInfo>
                        <ValueGeneralInfo>{notification?.sys_created_on?.value ? (dateStringFormat(notification?.date_incident?.value, 'DD/MM/YYYY')) : ("-")}</ValueGeneralInfo>
                    </div>
                    <div>
                        <LabelGeneralInfo>Data da ocorrência</LabelGeneralInfo>
                        <ValueGeneralInfo>{notification?.date_incident?.value ? (dateStringFormat(notification?.date_incident?.value, 'DD/MM/YYYY')) : ("-")}</ValueGeneralInfo>
                    </div>
                    <div>
                        <LabelGeneralInfo>Data de nascimento</LabelGeneralInfo>
                        <ValueGeneralInfo>{notification?.patient_date_of_birth?.value ? (dateStringFormat(notification?.patient_date_of_birth?.value, 'DD/MM/YYYY')) : ("-")}</ValueGeneralInfo>
                    </div>
                    <div>
                        <LabelGeneralInfo>Data da internação</LabelGeneralInfo>
                        <ValueGeneralInfo>{protocol?.date_of_patient_admission ? (dateStringFormat(notification?.date_of_patient_admission?.value, 'DD/MM/YYYY')) : ("-")}</ValueGeneralInfo>
                    </div>
                </div>
                <div className="flex flex-col">
                    <div >
                        <LabelGeneralInfo>Notificante</LabelGeneralInfo>
                        <ValueGeneralInfo>{notification?.reporter_name?.display_value || 'Anônimo'}</ValueGeneralInfo>
                    </div>
                    <div>
                        <LabelGeneralInfo>Setor do notificante</LabelGeneralInfo>
                        <ValueGeneralInfo>{notification?.incident_sector ? (notification?.incident_sector?.display_value) : ("-")}</ValueGeneralInfo>
                    </div>
                    <div>
                        <LabelGeneralInfo>Prontuário</LabelGeneralInfo>
                        <ValueGeneralInfo>
                            {getMedicalRecord}
                        </ValueGeneralInfo>
                    </div>
                    <div>
                        <LabelGeneralInfo>Diagnóstico do paciente</LabelGeneralInfo>
                        <ValueGeneralInfo>{getLabelByValue(typesOfDiagnosis, 'value', protocol?.types_of_diagnosis?.value)}</ValueGeneralInfo>
                    </div>
                </div>
                <div className="flex flex-col">
                    <div className='mt-16'>
                        <LabelGeneralInfo>Número do atendimento</LabelGeneralInfo>
                        <ValueGeneralInfo>
                            {getTreatmentNumber ? getTreatmentNumber : " - "}
                        </ValueGeneralInfo>
                    </div>

                </div>
                <div className="flex flex-col">
                    <div>
                        <LabelGeneralInfo>Setor</LabelGeneralInfo>
                        <ValueGeneralInfo> {notification?.incident_sector?.display_value}</ValueGeneralInfo>
                    </div>
                    <div>
                        <LabelGeneralInfo>Turno que o evento ocorreu</LabelGeneralInfo>
                        <ValueGeneralInfo>{notification?.shift_incident?.display_value}</ValueGeneralInfo>
                    </div>
                </div>
            </div>
            <div>
                <div>
                    <LabelGeneralInfo>Time de investigação do evento</LabelGeneralInfo>
                    <ValueGeneralInfo>{protocol?.event_investigation_team}</ValueGeneralInfo>
                </div>
            </div>
            <hr className={horizontalRowLine} />
            <div className='mt-4'>
                <Subtitle className='text-blueTheme font-bold'>
                    Análise do incidente
                </Subtitle>
                <div>
                    <LabelGeneralInfo>Problema identificado / Título do incidente</LabelGeneralInfo>
                    <ValueGeneralInfo>{notification?.incident_title?.display_value || "-"}</ValueGeneralInfo>
                </div>         
                <div>
                    <LabelGeneralInfo>Descrição do incidente</LabelGeneralInfo>
                    <ValueGeneralInfo>{notification?.incident_description?.display_value || "-"}</ValueGeneralInfo>
                </div>
                <div>
                    <LabelGeneralInfo>Ação corretiva imediata</LabelGeneralInfo>
                    <ValueGeneralInfo>{notification?.immediate_corrective_action?.display_value || "-"}</ValueGeneralInfo>
                </div>
            </div>
            <hr className={horizontalRowLine} />
            <div className='mt-4'>
                <Subtitle className='text-blueTheme font-bold'>
                    Detalhes do incidente
                </Subtitle>
                <div className='flex justify-between max-w-[800px]'>
                    <div>
                        <div>
                            <LabelGeneralInfo>Tipo de Incidente</LabelGeneralInfo>
                            <ValueGeneralInfo>{notification?.incident_type?.display_value}</ValueGeneralInfo>
                        </div>
                        <div>
                            <LabelGeneralInfo>Probabilidade de ocorrência do incidente</LabelGeneralInfo>
                            <ValueGeneralInfo>{getLabelByValue(londonVaribles?.probability, 'value', protocol?.probability)}</ValueGeneralInfo>
                        </div>
                    </div>
                    <div>
                        <div>
                            <LabelGeneralInfo>Grau do dano</LabelGeneralInfo>
                            <ValueGeneralInfo>{notification?.damage_level?.display_value}</ValueGeneralInfo>
                        </div>
                        <div>
                            <LabelGeneralInfo>Análise de tolerabilidade</LabelGeneralInfo>
                            <ValueGeneralInfo>{protocol?.tolerability_analysis}</ValueGeneralInfo>
                        </div>
                    </div>
                </div>

            </div>
            <hr className={horizontalRowLine} />
            <div className='mt-4'>
                <Subtitle className='text-blueTheme font-bold'>
                    Informações extras
                </Subtitle>
                <div className='mt-4'>
                    <LabelGeneralInfo>Classificação do incidente de acordo com a OMS - Organização Mundial de Saúde</LabelGeneralInfo>
                    <ValueGeneralInfo>{getLabelByValue(omsClassifications, 'value', notification?.u_oms_classification?.value)}</ValueGeneralInfo>
                </div>
                <div className='mt-4'>
                    <LabelGeneralInfo>Evento sentinela - (Manual Joint Comission International 7ª edição)</LabelGeneralInfo>
                    <ValueGeneralInfo>{getSentinelEvent}</ValueGeneralInfo>
                </div>
                <div className='mt-4'>
                    <LabelGeneralInfo>Never Events</LabelGeneralInfo>
                    <ValueGeneralInfo>{getNeverEvent}</ValueGeneralInfo>
                </div>
                <div className='mt-4'>
                    <LabelGeneralInfo>Consequências organizacionais</LabelGeneralInfo>
                    <ValueGeneralInfo key={protocol?.organizational_consequences || "default"}>
                        {getLabelByValue(londonVaribles?.organizational_consequences, 'value', protocol?.organizational_consequences) || "-"}
                    </ValueGeneralInfo>
                </div>
                <div className='mt-4'>
                    <LabelGeneralInfo>Relato cronológico do evento</LabelGeneralInfo>
                    {getChronologicalReport()?.map(item => <ValueGeneralInfo className='text-sm'>{item}</ValueGeneralInfo>)}
                </div>
                <div className='mt-4'>
                    <LabelGeneralInfo>Pontos fortes da assistência prestada e problemas no manejo dos cuidados</LabelGeneralInfo>
                    <ValueGeneralInfo key={protocol?.u_strengths_of_the_care_provided_and_problems_in_managing_care || "default"}>
                        {protocol?.u_strengths_of_the_care_provided_and_problems_in_managing_care || "-"}
                    </ValueGeneralInfo>                  
                </div>
                <div className='mt-4'>
                    <LabelGeneralInfo>Fonte de dados e métodos</LabelGeneralInfo>
                    <ValueGeneralInfo key={protocol?.u_data_source_and_methods || "default"}>
                        {protocol?.u_data_source_and_methods || "-"}
                    </ValueGeneralInfo>
                </div>
            </div>
            <hr className={horizontalRowLine} />
            <div className='mt-4'>
                <Subtitle className='text-blueTheme font-bold'>
                    Fatores contribuintes
                </Subtitle>
                {
                    checkBoxData.map((item, idx) => {
                        return (
                            <div key={idx} className='mt-4'>
                                <div className='mb-3'>
                                    <LabelGeneralInfo>{item.title}</LabelGeneralInfo>
                                    <ListGeneralInfo>
                                        {getFactorLabels(item.titleValue, item.mainObject)
                                        ?.split('; ') // Dividir a string em um array de itens
                                        ?.map((e, idx) => (
                                            <ListItemGeneralInfo key={idx} className="text-sm">{e}</ListItemGeneralInfo>
                                        ))}
                                    </ListGeneralInfo>
                                </div>
                                <div>
                                    <LabelGeneralInfo>Descrição</LabelGeneralInfo>
                                    <ValueGeneralInfo>{item.descriptionValue || "-"}</ValueGeneralInfo>
                                </div>
                            </div>
                        )
                    })
                }
                <hr className={horizontalRowLine} />
                <div className='mt-4'>
                    <div>
                        <LabelGeneralInfo>Parecer do NSP</LabelGeneralInfo>
                        <ValueGeneralInfo className='text-sm'>{protocoloNotificationFactors?.nsp_opinion?.filter(nsp => nsp?.value == protocol?.u_nsp_opinion)[0]?.label || "-"}</ValueGeneralInfo>
                    </div>
                    <div>
                        <LabelGeneralInfo>Descrição</LabelGeneralInfo>
                        <ValueGeneralInfo>{protocol?.u_description_nsp_opinion || "-"}</ValueGeneralInfo>
                    </div>
                </div>
            </div>
        </StaticDataContainer>
    )
}
